import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/problemDetails'
import {
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  type DataTableColumn,
  toast,
} from '@/components/ui'
import { translateEnumValue } from '@/lib/formatting'

import { useChangeMemberRole, useRemoveMember } from '../queries'
import { MEMBER_ROLES, type TeamMember } from '../types'

type StatusVariant = 'success' | 'warning' | 'danger' | 'info' | 'neutral'

const STATUS_VARIANTS: Readonly<Record<string, StatusVariant>> = {
  Active: 'success',
  Invited: 'info',
  Suspended: 'warning',
  Revoked: 'danger',
}

interface MembersTableProps {
  readonly tenantId: string
  readonly currentUserId: string | null
  readonly members: readonly TeamMember[]
}

/** Tabela de membros com nome/e-mail, alterar papel e remover. A própria linha do usuário é somente-leitura. */
export function MembersTable({ tenantId, currentUserId, members }: MembersTableProps) {
  const { t } = useTranslation()
  const changeRole = useChangeMemberRole(tenantId)
  const removeMember = useRemoveMember(tenantId)
  const [confirming, setConfirming] = useState<TeamMember | null>(null)

  const roleLabels: Readonly<Record<string, string>> = {
    Owner: t('team.roles.Owner'),
    Administrator: t('team.roles.Administrator'),
    Supervisor: t('team.roles.Supervisor'),
    Agent: t('team.roles.Agent'),
    Analyst: t('team.roles.Analyst'),
  }
  const statusLabels: Readonly<Record<string, string>> = {
    Invited: t('team.statuses.Invited'),
    Active: t('team.statuses.Active'),
    Suspended: t('team.statuses.Suspended'),
    Revoked: t('team.statuses.Revoked'),
  }

  const onError = (error: unknown) => {
    toast.error(error instanceof ApiError ? error.problem.title : t('team.actionFailed'))
  }

  const handleRoleChange = (membershipId: string, role: string) => {
    changeRole.mutate(
      { membershipId, role },
      {
        onSuccess: () => {
          toast.success(t('team.toast.roleChanged'))
        },
        onError,
      },
    )
  }

  const confirmRemove = () => {
    if (confirming === null) {
      return
    }
    const member = confirming
    removeMember.mutate(
      { membershipId: member.id },
      {
        onSuccess: () => {
          setConfirming(null)
          toast.success(t('team.toast.removed', { name: member.displayName }))
        },
        onError: (error) => {
          setConfirming(null)
          onError(error)
        },
      },
    )
  }

  const isSelf = (member: TeamMember): boolean =>
    currentUserId !== null && member.userId === currentUserId
  const busy = changeRole.isPending || removeMember.isPending

  const columns: readonly DataTableColumn<TeamMember>[] = [
    {
      key: 'member',
      header: t('team.memberLabel'),
      cell: (member) => (
        <div>
          <div className="font-medium text-foreground-primary">
            {member.displayName}
            {isSelf(member) ? (
              <span className="ml-2 text-xs font-normal text-foreground-muted">
                {t('team.you')}
              </span>
            ) : null}
          </div>
          {member.email !== null ? (
            <div className="text-xs text-foreground-muted">{member.email}</div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'role',
      header: t('team.roleLabel'),
      cell: (member) =>
        isSelf(member) ? (
          <Badge variant="neutral">{translateEnumValue(roleLabels, member.role)}</Badge>
        ) : (
          <select
            aria-label={t('team.roleLabel')}
            value={member.role}
            disabled={busy}
            onChange={(event) => {
              handleRoleChange(member.id, event.target.value)
            }}
            className="rounded-md border border-border-default bg-background-default px-2 py-1 text-sm text-foreground-primary outline-none focus:border-action-primary disabled:opacity-60"
          >
            {MEMBER_ROLES.map((role) => (
              <option key={role} value={role}>
                {translateEnumValue(roleLabels, role)}
              </option>
            ))}
          </select>
        ),
    },
    {
      key: 'status',
      header: t('team.statusLabel'),
      cell: (member) => (
        <Badge variant={STATUS_VARIANTS[member.status] ?? 'neutral'}>
          {translateEnumValue(statusLabels, member.status)}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: t('team.actionsLabel'),
      align: 'right',
      cell: (member) =>
        isSelf(member) ? (
          <span className="text-xs text-foreground-muted">—</span>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => {
              setConfirming(member)
            }}
          >
            {t('team.remove')}
          </Button>
        ),
    },
  ]

  return (
    <>
      <ConfirmDialog
        open={confirming !== null}
        title={t('team.removeConfirm.title')}
        description={t('team.removeConfirm.description', { name: confirming?.displayName ?? '' })}
        confirmLabel={t('team.removeConfirm.confirm')}
        cancelLabel={t('team.removeConfirm.cancel')}
        loading={removeMember.isPending}
        onConfirm={confirmRemove}
        onCancel={() => {
          setConfirming(null)
        }}
      />
      <DataTable
        columns={columns}
        rows={members}
        rowKey={(member) => member.id}
        caption={t('team.membersCaption')}
        minWidthClassName="min-w-[560px]"
      />
    </>
  )
}
